<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-container>
    <!-- Drop Downs List -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h2 class="text-h5 font-weight-bold">Drop Downs</h2>
          <div class="d-flex">
            <input
              ref="fileInput"
              type="file"
              accept=".json"
              @change="handleFileUpload"
              style="display: none"
            />
            <v-btn
              class="mr-2"
              color="primary"
              small
              @click="triggerFileUpload"
            >
              <v-icon small left>{{ icons.mdiUpload }}</v-icon>
              Upload Drop Down
            </v-btn>
            <v-btn
              color="primary"
              small
              @click="openAddDialog"
            >
              <v-icon small left>{{ icons.mdiPlus }}</v-icon>
              Add New Drop Down
            </v-btn>
          </div>
        </div>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="lists"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageOptions: [5, 10, 20, 50]
          }"
          class="elevation-3"
        >
          <template v-slot:item.DataDefinition="{ item }">
            {{ (item.DataDefinition || []).length }} items
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon
              small
              class="mr-2"
              @click="openEditDialog(item)"
            >
              {{ icons.mdiPencil }}
            </v-icon>
            <v-icon
              small
              @click="downloadList(item)"
            >
              {{ icons.mdiDownload }}
            </v-icon>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Add/Edit Dialog -->
    <v-dialog
      v-model="dialogVisible"
      max-width="70vw"
      persistent
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{ isEditing ? 'Edit List' : 'Add List' }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.Name"
                  label="Name *"
                  outlined
                  dense
                  :rules="[v => !!v || 'Name is required']"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Data Definitions Section -->
            <v-row>
              <v-col cols="12">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Data Definitions</h3>

                <div v-if="editedItem.DataDefinition && editedItem.DataDefinition.length > 0">
                  <v-card
                    v-for="(definition, index) in editedItem.DataDefinition"
                    :key="index"
                    class="mb-3 pa-3"
                    outlined
                  >
                    <div class="d-flex justify-space-between align-center mb-2">
                      <h4 class="text-subtitle-2 font-weight-medium">Definition {{ index + 1 }}</h4>
                      <v-btn
                        icon
                        x-small
                        color="error"
                        @click="removeDataDefinition(index)"
                      >
                        <v-icon>{{ icons.mdiDelete }}</v-icon>
                      </v-btn>
                    </div>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="definition.ShortName"
                          label="Short Name"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="definition.LongName"
                          label="Long Name"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4" class="d-flex align-center">
                        <v-checkbox
                          v-model="definition.Removed"
                          label="Removed"
                          dense
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
                <div v-else class="text-center text--secondary py-4">
                  No data definitions added yet
                </div>

                <v-btn
                  color="primary"
                  small
                  @click="addDataDefinition"
                  class="mt-3"
                >
                  <v-icon small left>{{ icons.mdiPlus }}</v-icon>
                  Add Definition
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialogVisible = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveList"
            :disabled="!isFormValid"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  mapActions,
} from 'vuex';
import {
  mdiPencil, mdiDownload, mdiUpload, mdiPlus, mdiDelete,
} from '@mdi/js';

export default {
  name: 'DropDowns',
  data: () => ({
    lists: [],
    loading: false,
    dialogVisible: false,
    isEditing: false,
    editedItem: {
      Name: '',
      DataDefinition: [],
    },
    headers: [
      {
        text: 'Name', value: 'Name', sortable: true,
      },
      {
        text: 'Data Definitions', value: 'DataDefinition', sortable: false,
      },
      {
        text: 'Actions', value: 'actions', sortable: false, align: 'center',
      },
    ],
    icons: {
      mdiPencil,
      mdiDownload,
      mdiUpload,
      mdiPlus,
      mdiDelete,
    },
  }),
  computed: {
    isFormValid() {
      return !!this.editedItem.Name;
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    ...mapActions([
      'flashSuccess',
      'flashError',
      'flashInfo',
    ]),
    ...mapActions('lists', [
      'loadLists',
      'upsertList',
      'deleteList',
    ]),

    // Load lists
    async loadData() {
      this.loading = true;
      try {
        const data = await this.loadLists({ criteria: {} });
        this.lists = data || [];
      } catch (error) {
        this.flashError('Failed to load lists');
        console.error('Error loading lists:', error);
      } finally {
        this.loading = false;
      }
    },

    // Open add dialog
    openAddDialog() {
      this.isEditing = false;
      this.editedItem = {
        Name: '',
        DataDefinition: [],
      };
      this.dialogVisible = true;
    },

    // Open edit dialog
    openEditDialog(item) {
      this.isEditing = true;
      this.editedItem = {
        ...item,
        DataDefinition: item.DataDefinition ? JSON.parse(JSON.stringify(item.DataDefinition)) : [],
      };
      this.dialogVisible = true;
    },

    // Add data definition
    addDataDefinition() {
      if (!this.editedItem.DataDefinition) {
        this.editedItem.DataDefinition = [];
      }
      this.editedItem.DataDefinition.push({
        ShortName: '',
        LongName: '',
        Removed: false,
      });
    },

    // Remove data definition
    removeDataDefinition(index) {
      this.editedItem.DataDefinition.splice(index, 1);
    },

    // Save list
    async saveList() {
      try {
        const item = { ...this.editedItem };
        delete item.__typename;

        // Clean up temporary UI properties from data definitions
        if (item.DataDefinition) {
          item.DataDefinition = item.DataDefinition.map((def) => {
            const cleanDef = { ...def };
            delete cleanDef.__typename;
            return cleanDef;
          });
        }

        // upsertList flashes its own error and resolves null when the save fails
        const saved = await this.upsertList({ item });
        if (!saved) return;
        this.flashSuccess(this.isEditing ? 'List updated successfully' : 'List created successfully');
        this.dialogVisible = false;
        await this.loadData();
      } catch (error) {
        this.flashError('Failed to save list');
        console.error('Error saving list:', error);
      }
    },

    // Download list
    downloadList(item) {
      try {
        // Create a clean copy without UI-specific properties
        const cleanItem = { ...item };
        delete cleanItem.__typename;

        if (cleanItem.DataDefinition) {
          cleanItem.DataDefinition = cleanItem.DataDefinition.map((def) => {
            const cleanDef = { ...def };
            delete cleanDef.__typename;
            return cleanDef;
          });
        }

        // Convert to JSON
        const jsonString = JSON.stringify(cleanItem, null, 2);

        // Create blob and download
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${item.Name.replace(/[^a-z0-9]/gi, '_')}_list.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.flashSuccess('List downloaded successfully');
      } catch (error) {
        this.flashError('Failed to download list');
        console.error('Error downloading list:', error);
      }
    },

    // Trigger file input click
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    // Handle file upload
    async handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      try {
        const fileContent = await file.text();
        const uploadedList = JSON.parse(fileContent);

        // Remove _id to create a new list instead of updating existing
        delete uploadedList._id;

        // Validate required fields
        if (!uploadedList.Name) {
          this.flashError('Invalid list: missing Name field');
          return;
        }

        // Open edit dialog with uploaded data
        this.isEditing = false;
        this.editedItem = {
          ...uploadedList,
          DataDefinition: uploadedList.DataDefinition || [],
        };

        this.dialogVisible = true;
        this.flashInfo('List loaded successfully. Review and save to create.');
      } catch (error) {
        this.flashError('Failed to parse list. Please ensure it is a valid JSON file.');
        console.error('Error parsing list:', error);
      } finally {
        // Reset file input
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      }
    },
  },
};
</script>

<style scoped>
</style>
